import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const user = await register(form);
      toast.success(`Welcome, ${user.name.split(" ")[0]}!`);
      navigate("/member");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50" data-testid="register-page">
      <div className="hidden lg:block relative bg-[#0F2744]">
        <img src="https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&q=85&w=1200" alt="Advanced driving" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 flex flex-col justify-end p-12">
          <ShieldCheck className="h-12 w-12 text-amber-400 mb-4" />
          <h2 className="text-3xl font-bold text-white">Create your member account</h2>
          <p className="mt-3 text-slate-300 max-w-md">Get access to event booking, training progress and club resources.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 mb-8" data-testid="register-home-link"><ArrowLeft className="h-4 w-4" /> Back to site</Link>
          <h1 className="text-3xl font-extrabold text-[#0F2744]">Create account</h1>
          <p className="mt-2 text-slate-500">Join the member area in seconds.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="register-form">
            <div>
              <Label className="mb-1.5 block">Full name</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="register-name" />
            </div>
            <div>
              <Label className="mb-1.5 block">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="register-email" />
            </div>
            <div>
              <Label className="mb-1.5 block">Phone (optional)</Label>
              <Input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="register-phone" />
            </div>
            <div>
              <Label className="mb-1.5 block">Password</Label>
              <Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required data-testid="register-password" />
            </div>
            {error && <p className="text-sm text-red-600" data-testid="register-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full bg-[#0F2744] hover:bg-[#0B192C] h-12" data-testid="register-submit">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create account"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-slate-500 text-center">
            Already a member? <Link to="/login" className="font-semibold text-blue-600" data-testid="register-login-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
